import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "fixed inset-0 z-10 overflow-y-auto" }
const _hoisted_2 = { class: "flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0" }
const _hoisted_3 = { class: "my-3 text-center sm:mt-0 sm:text-left flex flex-wrap gap-4 items-center justify-between border-b border-gray-200 pb-4" }
const _hoisted_4 = {
  key: 0,
  class: "mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md"
}
const _hoisted_5 = {
  key: 0,
  class: "w-full flex justify-center items-center py-20"
}
const _hoisted_6 = {
  key: 1,
  class: "w-full flex items-center justify-center bg-gray-100 p-4 rounded-lg gap-4"
}
const _hoisted_7 = ["disabled"]
const _hoisted_8 = { class: "bg-white shadow-lg rounded-lg overflow-hidden" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = {
  key: 2,
  class: "mt-4 flex flex-col items-center gap-4"
}
const _hoisted_11 = { class: "flex items-center" }
const _hoisted_12 = { class: "text-sm font-medium text-gray-700" }

import { ref, onMounted, onUnmounted, watch, nextTick } from "vue";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";

import * as PDFJS from "../../../../../public/scripts/pdf.min.js";

export default /*@__PURE__*/_defineComponent({
  __name: 'SecurePdfViewerModal',
  props: {
    pdfUrl: {},
    visible: { type: Boolean },
    filename: {},
    isConverted: { type: Boolean }
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

PDFJS.GlobalWorkerOptions.workerSrc = "/scripts/pdf.worker.min.js";

const props = __props;

const canvas = ref<HTMLCanvasElement | null>(null);
const numPages = ref(0);
const currentPage = ref(0);
const scale = ref(1);
const isLoading = ref(false);
let pdfDocument: any = null;

const emit = __emit;

const loadPdf = async () => {
  try {
    isLoading.value = true;
    const pdf = await PDFJS.getDocument(props.pdfUrl).promise;
    pdfDocument = pdf;
    numPages.value = pdf.numPages;
    if (numPages.value > 0) {
      currentPage.value = 1;
    }
  } catch (error) {
    console.error("Error loading PDF:", error);
  } finally {
    isLoading.value = false;
    // The watcher will handle rendering when loading is complete
  }
};

const renderPage = async () => {
  try {
    console.log("renderPage called:", {
      hasCanvas: !!canvas.value,
      hasPdfDocument: !!pdfDocument,
      currentPage: currentPage.value,
      numPages: numPages.value,
    });

    if (!canvas.value || !pdfDocument || currentPage.value <= 0) {
      console.log("renderPage early return");
      return;
    }

    const ctx = canvas.value.getContext("2d");
    if (!ctx) {
      console.log("No canvas context");
      return;
    }

    console.log("Getting page:", currentPage.value);
    const page = await pdfDocument.getPage(currentPage.value);

    const viewport = page.getViewport({ scale: scale.value });
    canvas.value.width = viewport.width;
    canvas.value.height = viewport.height;

    console.log("Rendering page with viewport:", {
      width: viewport.width,
      height: viewport.height,
      scale: scale.value,
    });
    await page.render({
      canvasContext: ctx,
      viewport,
    }).promise;

    console.log("Page rendered successfully");
  } catch (error) {
    console.error("Error rendering PDF page:", error);
  }
};

const previousPage = async () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    await renderPage();
  }
};

const nextPage = async () => {
  if (currentPage.value < numPages.value) {
    currentPage.value++;
    await renderPage();
  }
};

const zoomIn = async () => {
  if (scale.value < 3) {
    scale.value = Math.min(scale.value + 0.25, 3);
    await renderPage();
  }
};

const zoomOut = async () => {
  if (scale.value > 0.5) {
    scale.value = Math.max(scale.value - 0.25, 0.5);
    await renderPage();
  }
};

// Canvas click handlers for zoom
const handleCanvasClick = async (event: MouseEvent) => {
  event.preventDefault();
  await zoomIn();
};

const handleCanvasRightClick = async (event: MouseEvent) => {
  event.preventDefault();
  await zoomOut();
};

// Keyboard navigation
const handleKeydown = (event: KeyboardEvent) => {
  if (!props.visible) return;

  switch (event.key) {
    case "ArrowLeft":
      event.preventDefault();
      previousPage();
      break;
    case "ArrowRight":
      event.preventDefault();
      nextPage();
      break;
    case "+":
    case "=":
      event.preventDefault();
      zoomIn();
      break;
    case "-":
      event.preventDefault();
      zoomOut();
      break;
    case "Escape":
      event.preventDefault();
      emit("close");
      break;
  }
};

// Load PDF when component mounts and when props change
onMounted(() => {
  // Add keyboard event listener
  document.addEventListener("keydown", handleKeydown);

  if (props.visible) {
    loadPdf();
  }
});

onUnmounted(() => {
  // Remove keyboard event listener
  document.removeEventListener("keydown", handleKeydown);
});

// Watch for changes in visibility and pdfUrl
watch(
  () => [props.visible, props.pdfUrl],
  ([newVisible, newPdfUrl], [oldVisible, oldPdfUrl]) => {
    if (
      newVisible &&
      newPdfUrl &&
      (newVisible !== oldVisible || newPdfUrl !== oldPdfUrl)
    ) {
      loadPdf();
    }
  }
);

// Watch for when loading finishes and canvas becomes available
watch(
  () => [isLoading.value, canvas.value],
  async ([newIsLoading, newCanvas]) => {
    if (!newIsLoading && newCanvas && pdfDocument && currentPage.value > 0) {
      console.log("Canvas available after loading, rendering page");
      await nextTick();
      await renderPage();
    }
  }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(TransitionRoot), {
    as: "template",
    show: __props.visible
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(Dialog), {
        as: "div",
        class: "relative z-50",
        onClose: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('close')))
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(TransitionChild), {
            as: "template",
            enter: "ease-out duration-300",
            "enter-from": "opacity-0",
            "enter-to": "opacity-100",
            leave: "ease-in duration-200",
            "leave-from": "opacity-100",
            "leave-to": "opacity-0"
          }, {
            default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
              _createElementVNode("div", { class: "fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" }, null, -1)
            ]))]),
            _: 1
          }),
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_unref(TransitionChild), {
                as: "template",
                enter: "ease-out duration-300",
                "enter-from": "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95",
                "enter-to": "opacity-100 translate-y-0 sm:scale-100",
                leave: "ease-in duration-200",
                "leave-from": "opacity-100 translate-y-0 sm:scale-100",
                "leave-to": "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(DialogPanel), { class: "relative transform overflow-auto rounded-lg bg-white px-6 pt-6 pb-6 text-left shadow-xl transition-all sm:my-8 sm:w-11/12 sm:max-w-6xl sm:p-8 max-h-[calc(100vh-100px)]" }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_3, [
                        _createElementVNode("div", null, [
                          _createVNode(_unref(DialogTitle), {
                            as: "h3",
                            class: "text-lg font-semibold leading-6 text-gray-900"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(props.filename || "PDF Förhandsgranskning"), 1)
                            ]),
                            _: 1
                          }),
                          _cache[4] || (_cache[4] = _createElementVNode("p", { class: "text-sm text-gray-500 mt-1" }, " Använd piltangenterna för att navigera, klicka för att zooma in, högerklicka för att zooma ut, ESC för att stänga ", -1)),
                          (props.isConverted)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [...(_cache[3] || (_cache[3] = [
                                _createElementVNode("p", { class: "text-sm text-yellow-800" }, [
                                  _createElementVNode("i", { class: "fas fa-exclamation-triangle mr-1" }),
                                  _createElementVNode("strong", null, "Konverterad förhandsgranskning:"),
                                  _createTextVNode(" Detta är en konverterad version som kanske inte är 100% sann mot originalet. För att se originalfilen, använd nedladdningsknappen i fillistan. ")
                                ], -1)
                              ]))]))
                            : _createCommentVNode("", true)
                        ]),
                        _createElementVNode("i", {
                          onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close'))),
                          class: "text-2xl font-bold fas fa-close cursor-pointer hover:text-gray-600 transition-colors"
                        })
                      ]),
                      (isLoading.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [...(_cache[5] || (_cache[5] = [
                            _createElementVNode("div", { class: "flex flex-col items-center gap-4" }, [
                              _createElementVNode("div", { class: "animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" }),
                              _createElementVNode("p", { class: "text-gray-600" }, "Laddar PDF...")
                            ], -1)
                          ]))]))
                        : _createCommentVNode("", true),
                      (!isLoading.value && numPages.value > 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                            _createElementVNode("button", {
                              onClick: previousPage,
                              disabled: currentPage.value <= 1,
                              class: "flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-lg",
                              title: "Föregående sida"
                            }, [...(_cache[6] || (_cache[6] = [
                              _createElementVNode("i", { class: "fas fa-chevron-left text-lg" }, null, -1)
                            ]))], 8, _hoisted_7),
                            _createElementVNode("div", _hoisted_8, [
                              _createElementVNode("canvas", {
                                ref_key: "canvas",
                                ref: canvas,
                                class: "max-w-full h-auto border border-gray-200 cursor-pointer",
                                onClick: handleCanvasClick,
                                onContextmenu: handleCanvasRightClick
                              }, null, 544)
                            ]),
                            _createElementVNode("button", {
                              onClick: nextPage,
                              disabled: currentPage.value >= numPages.value,
                              class: "flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-lg",
                              title: "Nästa sida"
                            }, [...(_cache[7] || (_cache[7] = [
                              _createElementVNode("i", { class: "fas fa-chevron-right text-lg" }, null, -1)
                            ]))], 8, _hoisted_9)
                          ]))
                        : _createCommentVNode("", true),
                      (!isLoading.value && numPages.value > 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                            _createElementVNode("div", _hoisted_11, [
                              _createElementVNode("span", _hoisted_12, " Sida " + _toDisplayString(currentPage.value) + " av " + _toDisplayString(numPages.value), 1)
                            ])
                          ]))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ])
          ])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["show"]))
}
}

})